import React, { Suspense } from 'react';
import { Navigate, Outlet, Route } from 'react-router';
import { Icons } from '../../../../config';
import { createReportingRouter } from '../route';
import { ReportingShellConfig, ReportingShellInit } from './reportingShellConfig';
import { createReportingRootContainer } from './page/root/reportingRootContainer';
import { createReportingRootController } from './page/root/reportingRootController';
import { createReportingRootLoader } from './page/root/reportingRootLoader';
import {
    ReportingShellContainerBaseConfig,
    ReportingShellControllerBaseConfig,
    ReportingShellLoaderBaseConfig,
    ReportingShellPubsub,
} from './base';
import {
    createNotificationListController,
    createReportingAccountController,
    createReportingNavigationController,
    createShortcutListController,
    createTaskListController,
} from './view';
import {
    ReportingShellComponents,
    ReportingShellEnhancer,
} from './reportingShellInterface';
import {
    ReportingShellContext,
    createReportingContextController,
    creatReportingContextLoader,
    createReportingErrorContainer,
    createReportingErrorController,
    createReportingShellContextContainer,
    createReportingShellContextProvider,
    createChatOverlayContainer,
    createChatOverlayController,
    createChatOverlayLoader,
    createChatOverlayView,
    createChatDetailContainer,
    createChatDetailController,
    createChatDetailView,
    createChatDetailLoader,
    createChatApplyController,
    createChatApplyContainer,
    createChatApplyLoader,
    createChatItemCreateContainer,
    createChatItemCreateLoader,
    createChatItemCreateController,
    createChatDetailContext,
    createChatComposerContainer,
    createChatComposerController,
    createChatComposerLoader,
    createChatCreateContainer,
    createChatCreateController,
    createChatCreateLoader,
    createChatCreateView,
    createChatLayoutView,
    createChatComposerView,
    createChatItemSearchLoader,
    createChatItemSearchContainer,
    createChatItemSearchController,
    createChatItemReadContainer,
    createChatItemReadLoader,
    createChatItemReadController,
    createChatItemTaskController,
    createChatItemTaskLoader,
    createChatItemTaskContainer,
    createChatItemReplaceContainer,
    createChatItemReplaceLoader,
    createChatItemReplaceController,
    createChatErrorContainer,
    createChatErrorController,
    createChatErrorLoader,
} from './page';
import { Center, Grid, GridItem, HStack, VStack } from '@chakra-ui/react';
import { createEventProvider } from '@varos/client-base';
import { CardSpinner } from '../../../../domain';
import {
    createReportingSidebarContainer,
    createReportingSidebarLoader,
    createReportingSidebarController,
    createReportingSidebarView,
} from './page/sidebar';
import { createTreeItemContainer } from './view/tree/item';
import { createTreeItemController } from './view/tree/item';
import { createReportingShellStateProvider } from './reportingShellState';
import { createChatLayoutLoader } from './page/chat/layout/chatLayoutLoader';
import { createChatLayoutContainer } from './page/chat/layout';
import { createChatLayoutController } from './page/chat/layout';
import { HiOutlineHome } from 'react-icons/hi';
import { GoWorkflow } from 'react-icons/go';

export function createReportingShell(
    config: ReportingShellInit,
    enhancer?: ReportingShellEnhancer
) {
    return create(
        {
            ...config,
            provider: {
                createRootController: createReportingRootController,
                createNavigationController: createReportingNavigationController,
                createAccountController: createReportingAccountController,
                createNotificationController: createNotificationListController,
                createShortcutController: createShortcutListController,
                createReportingRouter: createReportingRouter,
                createTaskListController: createTaskListController,
            },
            enhancer: {
                reporting: null,
            },
        },
        enhancer
    );
}

function create(
    config: ReportingShellConfig,
    enhancer?: ReportingShellEnhancer
): ReportingShellComponents {
    if (enhancer) {
        return enhancer(create)(config);
    }
    const Context = React.createContext<ReportingShellContext | null>(null);

    const loaderConfig: ReportingShellLoaderBaseConfig = {
        kernel: config.kernel,
        repository: config.repository,
        hook: config.hook,
    };

    const controllerConfig: ReportingShellControllerBaseConfig = {
        kernel: config.kernel,
        view: config.view,
        hook: config.hook,
    };

    const states = createReportingShellStateProvider();

    const contexts = createReportingShellContextProvider(
        createReportingShellContextContainer(
            config,
            Context,
            creatReportingContextLoader(loaderConfig),
            createReportingContextController(controllerConfig)
        ),
        Context
    );
    const pubsub: ReportingShellPubsub = {
        router: createEventProvider(),
    };

    const containerConfig: ReportingShellContainerBaseConfig = {
        basePath: config.basePath,
        kernel: config.kernel,
        context: contexts,
        Theme: config.Theme,
        view: config.view,
        UI: config.UI,
        repository: config.repository,
        state: states,
        pubsub,
        hook: config.hook,
    };

    const loader = createReportingRootLoader(loaderConfig);
    const controller = config.provider.createRootController({
        ...config,
        controller: {
            navigation: config.provider.createNavigationController(controllerConfig),
            account: config.provider.createAccountController(controllerConfig),
            shortcut: config.provider.createShortcutController(controllerConfig),
            notification: config.provider.createNotificationController(controllerConfig),
            task: config.provider.createTaskListController(controllerConfig),
        },
    });

    const SidebarContainer = createReportingSidebarContainer(
        {
            ...containerConfig,
            navigation: {
                items: [
                    {
                        id: 'home',
                        label: 'Home',
                        Icon: HiOutlineHome,
                        path: '/reporting/home',
                    },
                    {
                        id: 'tasks',
                        label: 'Tasks',
                        Icon: GoWorkflow,
                        path: '/reporting/tasks',
                    },
                ],
            },
        },
        createReportingSidebarLoader(loaderConfig),
        createReportingSidebarController(config)
    );

    const TreeItemContainer = createTreeItemContainer(
        containerConfig,
        createTreeItemController(controllerConfig)
    );

    const Shell = createReportingRootContainer(
        {
            ...containerConfig,
            shortcuts: {
                items: [
                    {
                        id: 'shortcut',
                        label: 'New report',
                        kind: 'link',
                        variant: 'primary',
                        Icon: Icons.Reports.Report,
                        path: `/${config.basePath}/home`,
                    },
                ],
            },
            navigation: {
                // items: navigationItems,
                items: [],
            },
            Container: {
                Error: createReportingErrorContainer(
                    containerConfig,
                    createReportingErrorController()
                ),
            },
        },
        loader,
        controller
    );

    const Layout = {
        Centered: config.layout.centered.create({}),
    };

    const {
        Pages: {
            Report: ReportPage,
            Content: ContentPage,
            Record: RecordPage,
            Task: TaskPage,
        },
    } = config.provider.createReportingRouter(
        {
            UI: config.UI,
            Layout,
            kernel: config.kernel,
            context: contexts,
            controller: config.view,
            repository: config.repository,
            state: states,
            pubsub: pubsub.router,
        },
        config.enhancer.reporting ?? undefined
    );

    const { Provider: ContextProvider } = contexts;

    const Sidebar = createReportingSidebarView({
        ...config,
        Container: {
            Tree: TreeItemContainer,
        },
    });

    const context = {
        chat: {
            context: createChatDetailContext(),
        },
    };

    const Composer = createChatComposerContainer(
        {
            ...containerConfig,
            chat: context.chat,
        },
        createChatComposerLoader(loaderConfig),
        createChatComposerController(config),
        createChatComposerView({
            ...config,
        })
    );

    const Chat = {
        Overlay: createChatOverlayContainer(
            containerConfig,
            createChatOverlayLoader(loaderConfig),
            createChatOverlayController(config),
            createChatOverlayView({
                ...config,
                Container: {
                    Layout: createChatLayoutContainer(
                        {
                            ...containerConfig,
                            chat: context.chat,
                        },
                        createChatLayoutLoader(loaderConfig),
                        createChatLayoutController(config),
                        createChatLayoutView({
                            ...config,
                            Container: {
                                Error: createChatErrorContainer(
                                    containerConfig,
                                    createChatErrorLoader(loaderConfig),
                                    createChatErrorController(config)
                                ),
                            },
                        })
                    ),
                    Detail: createChatDetailContainer(
                        {
                            ...containerConfig,
                            chat: context.chat,
                        },
                        createChatDetailLoader(loaderConfig),
                        createChatDetailController(config),
                        createChatDetailView({
                            ...config,
                            Container: {
                                Item: {
                                    Replace: createChatItemReplaceContainer(
                                        containerConfig,
                                        createChatItemReplaceLoader(loaderConfig),
                                        createChatItemReplaceController()
                                    ),
                                    Apply: createChatApplyContainer(
                                        containerConfig,
                                        createChatApplyLoader(loaderConfig),
                                        createChatApplyController()
                                    ),
                                    Create: createChatItemCreateContainer(
                                        containerConfig,
                                        createChatItemCreateLoader(loaderConfig),
                                        createChatItemCreateController()
                                    ),
                                    Search: createChatItemSearchContainer(
                                        containerConfig,
                                        createChatItemSearchLoader(loaderConfig),
                                        createChatItemSearchController()
                                    ),
                                    Read: createChatItemReadContainer(
                                        containerConfig,
                                        createChatItemReadLoader(loaderConfig),
                                        createChatItemReadController()
                                    ),
                                    Task: createChatItemTaskContainer(
                                        {
                                            ...containerConfig,
                                            navigation: {
                                                task(props) {
                                                    return {
                                                        path: `/${config.basePath}/tasks`,
                                                    };
                                                },
                                            },
                                        },
                                        createChatItemTaskLoader(loaderConfig),
                                        createChatItemTaskController()
                                    ),
                                },
                                Composer,
                            },
                        })
                    ),
                    Create: createChatCreateContainer(
                        containerConfig,
                        createChatCreateLoader(loaderConfig),
                        createChatCreateController(config),
                        createChatCreateView({
                            ...config,
                            Container: {
                                Composer,
                            },
                        })
                    ),
                },
            })
        ),
    };

    const LayoutView: React.FC<{ children: React.ReactNode }> = states.chat.subscribe(
        (props) => {
            const context = contexts.useContext();
            const state = states.chat.useState(context);
            const layoutState = states.layout.useState(context);

            const [isScrolling, setIsScrolling] = React.useState(false);
            const scrollTimeoutRef = React.useRef<NodeJS.Timeout>();

            const handleScroll = React.useCallback(() => {
                setIsScrolling(true);

                // Clear existing timeout
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }

                // Set new timeout to hide scrollbar
                scrollTimeoutRef.current = setTimeout(() => {
                    setIsScrolling(false);
                }, 1000); // Hide after 1 second of no scrolling
            }, []);

            React.useEffect(() => {
                return () => {
                    if (scrollTimeoutRef.current) {
                        clearTimeout(scrollTimeoutRef.current);
                    }
                };
            }, []);

            // Get chat width from state, with fallback to default
            const chatWidth = layoutState.value.chatWidth || 384; // 24rem default

            return (
                <>
                    <Grid
                        position="absolute"
                        top={0}
                        bottom={0}
                        left={0}
                        right={0}
                        templateColumns={
                            state.value.isOpen ? `1fr ${chatWidth}px` : '1fr 0'
                        }
                    >
                        <GridItem
                            overflowY="auto"
                            pb={24}
                            onScroll={handleScroll}
                            sx={{
                                scrollbarGutter: 'stable',
                                '&::-webkit-scrollbar': {
                                    '-webkit-appearance': 'none',
                                    width: '0.5rem',
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: 'transparent',
                                },
                                '&::-webkit-scrollbar-corner': {
                                    '-webkit-appearance': 'none',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    borderRadius: '1rem',
                                    background: 'rgba(255,255,255,0.3)',
                                    display: isScrolling ? 'block' : 'none',
                                },
                            }}
                        >
                            <Suspense
                                fallback={
                                    <Center h="full" w="full">
                                        <CardSpinner />
                                    </Center>
                                }
                            >
                                {props.children}
                            </Suspense>
                        </GridItem>
                        <GridItem>
                            <Suspense
                                fallback={
                                    <Center h="full" w="full">
                                        <CardSpinner />
                                    </Center>
                                }
                            >
                                <Chat.Overlay
                                    initialWidth={{ base: '20rem', xl: '25dvw' }}
                                    minWidth={{ base: '12rem' }}
                                    maxWidth={{ base: '50dvw', xl: '33dvw' }}
                                />
                            </Suspense>
                        </GridItem>
                    </Grid>
                    <ContentPage.Overlay />
                    <RecordPage.Overlay />
                </>
            );
        }
    );

    const element = (
        <Route
            path={config.basePath}
            element={
                <ContextProvider>
                    <Shell>
                        <Grid
                            pos="absolute"
                            top={0}
                            bottom={0}
                            left={0}
                            right={0}
                            templateColumns={{
                                sm: '1fr',
                                md: 'min-content 1fr',
                            }}
                            gap={0}
                        >
                            <GridItem
                                overflowY="scroll"
                                display={{ sm: 'none', md: 'block' }}
                            >
                                <SidebarContainer
                                    as={Sidebar}
                                    initialWidth={{ base: '20rem', xl: '25dvw' }}
                                    minWidth={{ base: '12rem' }}
                                    maxWidth={{ base: '50dvw', xl: '33dvw' }}
                                />
                            </GridItem>
                            <GridItem position="relative">
                                <Outlet />
                            </GridItem>
                        </Grid>
                    </Shell>
                </ContextProvider>
            }
        >
            <Route index={true} element={<Navigate to="home" replace={true} />} />
            <Route path="home" element={<ReportPage.Generate />} />
            <Route
                path="tasks"
                element={
                    <LayoutView>
                        <TaskPage.List />
                    </LayoutView>
                }
            />
            <Route
                path="reports"
                element={
                    <LayoutView>
                        <Outlet />
                    </LayoutView>
                }
            >
                <Route path=":contentId" element={<ContentPage.Detail />} />
            </Route>
        </Route>
    );

    return {
        element,
    };
}
